import { dbService } from './database';
import { GameEngine } from '../game/state/gameEngine';
import { boxKey, cellKey, cellToBox } from '../game/core/coordinates';
import { BoxStatus } from '../game/core/box';
import { CellState } from '../game/core/cell';

export class GameRepository {
  public async loadGame(): Promise<GameEngine> {
    const db = await dbService.getDb();

    if (!db) {
      // LocalStorage fallback for Web Dev mode
      const seed = localStorage.getItem('game_seed') || Math.random().toString(36).substring(2, 10);
      const rewardPoints = parseInt(localStorage.getItem('game_reward_points') || '0', 10);
      const failureCount = parseInt(localStorage.getItem('game_failure_count') || '0', 10);
      const cameraX = parseFloat(localStorage.getItem('game_camera_x') || '0');
      const cameraY = parseFloat(localStorage.getItem('game_camera_y') || '0');
      const zoom = parseFloat(localStorage.getItem('game_zoom') || '1');

      const engine = new GameEngine(seed, rewardPoints, failureCount, cameraX, cameraY, zoom);

      try {
        const rawBoxes = localStorage.getItem('game_boxes');
        if (rawBoxes) {
          const boxesObj = JSON.parse(rawBoxes);
          for (const [k, v] of Object.entries(boxesObj)) {
            engine.boxes.set(k, v as BoxStatus);
          }
        }

        const rawCells = localStorage.getItem('game_cells');
        if (rawCells) {
          const cellsObj = JSON.parse(rawCells);
          for (const [k, v] of Object.entries(cellsObj)) {
            engine.cells.set(k, v as CellState);
          }
        }
      } catch {
        // Ignore JSON parse errors
      }

      return engine;
    }

    // Native SQLite implementation
    const gameResult = await db.query('SELECT * FROM game WHERE id = 1');
    let engine: GameEngine;

    if (!gameResult.values || gameResult.values.length === 0) {
      const seed = Math.random().toString(36).substring(2, 10);
      engine = new GameEngine(seed, 0, 0, 0, 0, 1);

      await db.run(
        `INSERT INTO game (id, seed, reward_points, failure_count, camera_x, camera_y, zoom)
         VALUES (1, ?, 0, 0, 0, 0, 1)`,
        [seed]
      );
      await dbService.saveStore();
    } else {
      const g = gameResult.values[0];
      engine = new GameEngine(
        g.seed,
        g.reward_points,
        g.failure_count,
        g.camera_x,
        g.camera_y,
        g.zoom
      );
    }

    const boxesResult = await db.query('SELECT * FROM boxes');
    if (boxesResult.values) {
      for (const row of boxesResult.values) {
        engine.boxes.set(boxKey(row.box_x, row.box_y), row.status as BoxStatus);
      }
    }

    const cellsResult = await db.query('SELECT * FROM cells');
    if (cellsResult.values) {
      for (const row of cellsResult.values) {
        const x = row.box_x * 10 + row.cell_x;
        const y = row.box_y * 10 + row.cell_y;
        engine.cells.set(cellKey(x, y), row.state as CellState);
      }
    }

    return engine;
  }

  public async saveGameState(engine: GameEngine): Promise<void> {
    localStorage.setItem('game_seed', engine.seed);
    localStorage.setItem('game_reward_points', engine.rewardPoints.toString());
    localStorage.setItem('game_failure_count', engine.failureCount.toString());
    localStorage.setItem('game_camera_x', engine.cameraX.toString());
    localStorage.setItem('game_camera_y', engine.cameraY.toString());
    localStorage.setItem('game_zoom', engine.zoom.toString());

    const db = await dbService.getDb();
    if (!db) return;

    await db.run(
      `UPDATE game SET
         seed = ?,
         reward_points = ?,
         failure_count = ?,
         camera_x = ?,
         camera_y = ?,
         zoom = ?
       WHERE id = 1`,
      [
        engine.seed,
        engine.rewardPoints,
        engine.failureCount,
        engine.cameraX,
        engine.cameraY,
        engine.zoom,
      ]
    );
    await dbService.saveStore();
  }

  public async saveBoxStatus(boxX: number, boxY: number, status: BoxStatus): Promise<void> {
    const rawBoxes = localStorage.getItem('game_boxes');
    const boxesObj = rawBoxes ? JSON.parse(rawBoxes) : {};
    boxesObj[boxKey(boxX, boxY)] = status;
    localStorage.setItem('game_boxes', JSON.stringify(boxesObj));

    const db = await dbService.getDb();
    if (!db) return;

    await db.run(
      `INSERT INTO boxes (box_x, box_y, status) VALUES (?, ?, ?)
       ON CONFLICT(box_x, box_y) DO UPDATE SET status = excluded.status`,
      [boxX, boxY, status]
    );
    await dbService.saveStore();
  }

  public async saveCellState(x: number, y: number, state: CellState): Promise<void> {
    const rawCells = localStorage.getItem('game_cells');
    const cellsObj = rawCells ? JSON.parse(rawCells) : {};
    const key = cellKey(x, y);
    if (state === 'HIDDEN') {
      delete cellsObj[key];
    } else {
      cellsObj[key] = state;
    }
    localStorage.setItem('game_cells', JSON.stringify(cellsObj));

    const db = await dbService.getDb();
    if (!db) return;

    const { boxX, boxY, cellX, cellY } = cellToBox(x, y);
    if (state === 'HIDDEN') {
      await db.run(
        `DELETE FROM cells WHERE box_x = ? AND box_y = ? AND cell_x = ? AND cell_y = ?`,
        [boxX, boxY, cellX, cellY]
      );
    } else {
      await db.run(
        `INSERT INTO cells (box_x, box_y, cell_x, cell_y, state) VALUES (?, ?, ?, ?, ?)
         ON CONFLICT(box_x, box_y, cell_x, cell_y) DO UPDATE SET state = excluded.state`,
        [boxX, boxY, cellX, cellY, state]
      );
    }
    await dbService.saveStore();
  }

  public async saveCellsBatch(cells: { x: number; y: number; state: CellState }[]): Promise<void> {
    const rawCells = localStorage.getItem('game_cells');
    const cellsObj = rawCells ? JSON.parse(rawCells) : {};
    for (const c of cells) {
      const key = cellKey(c.x, c.y);
      if (c.state === 'HIDDEN') {
        delete cellsObj[key];
      } else {
        cellsObj[key] = c.state;
      }
    }
    localStorage.setItem('game_cells', JSON.stringify(cellsObj));

    const db = await dbService.getDb();
    if (!db) return;

    for (const c of cells) {
      const { boxX, boxY, cellX, cellY } = cellToBox(c.x, c.y);
      if (c.state === 'HIDDEN') {
        await db.run(
          `DELETE FROM cells WHERE box_x = ? AND box_y = ? AND cell_x = ? AND cell_y = ?`,
          [boxX, boxY, cellX, cellY]
        );
      } else {
        await db.run(
          `INSERT INTO cells (box_x, box_y, cell_x, cell_y, state) VALUES (?, ?, ?, ?, ?)
           ON CONFLICT(box_x, box_y, cell_x, cell_y) DO UPDATE SET state = excluded.state`,
          [boxX, boxY, cellX, cellY, c.state]
        );
      }
    }
    await dbService.saveStore();
  }

  public async clearBoxCells(boxX: number, boxY: number): Promise<void> {
    const rawCells = localStorage.getItem('game_cells');
    if (rawCells) {
      const cellsObj = JSON.parse(rawCells);
      for (let cy = 0; cy < 10; cy++) {
        for (let cx = 0; cx < 10; cx++) {
          delete cellsObj[cellKey(boxX * 10 + cx, boxY * 10 + cy)];
        }
      }
      localStorage.setItem('game_cells', JSON.stringify(cellsObj));
    }

    const db = await dbService.getDb();
    if (!db) return;

    await db.run(`DELETE FROM cells WHERE box_x = ? AND box_y = ?`, [boxX, boxY]);
    await dbService.saveStore();
  }

  public async resetGameData(newSeed: string): Promise<void> {
    localStorage.removeItem('game_boxes');
    localStorage.removeItem('game_cells');
    localStorage.setItem('game_seed', newSeed);
    localStorage.setItem('game_reward_points', '0');
    localStorage.setItem('game_failure_count', '0');
    localStorage.setItem('game_camera_x', '0');
    localStorage.setItem('game_camera_y', '0');
    localStorage.setItem('game_zoom', '1');

    const db = await dbService.getDb();
    if (!db) return;

    await db.run('DELETE FROM boxes');
    await db.run('DELETE FROM cells');
    await db.run(
      `UPDATE game SET
         seed = ?,
         reward_points = 0,
         failure_count = 0,
         camera_x = 0,
         camera_y = 0,
         zoom = 1
       WHERE id = 1`,
      [newSeed]
    );
    await dbService.saveStore();
  }
}

export const gameRepository = new GameRepository();
